/**
 * 标签管理 · Tag Management
 *
 * 2026-07-31 · Two-pane layout mirroring the mockup:
 *   Left  → tag dictionary CRUD (list · search · add · rename · recolor · delete · reorder)
 *   Right → products currently carrying the selected tag (grid, paginated)
 *
 * Access control: admin-only for CRUD; staff can VIEW the list and filter products.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, MoreHorizontal, Trash2, Edit2, Palette, ChevronLeft, ChevronRight, Star, Check, PanelLeftClose, PanelLeftOpen, FilePlus2, Share2, Users, UserRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { TagPickerDialog, ShareTagDialog } from "@/components/TagPickerDialog";
import { QuotePicker } from "@/components/QuotePicker";
import { photoThumb } from "@/lib/photoUrl";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useQuote } from "@/context/QuoteContext";

const PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16", "#06b6d4", "#a855f7"];

export default function TagsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  // 2026-02 · Bulk add to quote — user replaced the "批量加标签" button with
  // "加入报价单" per spec. Uses the same QuoteContext.addToQuote helper as
  // ProductsList so behaviour (auto-active quote, item merging) matches.
  const { addToQuote, activeQuote } = useQuote();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [addBusy, setAddBusy] = useState(false);
  const isAdmin = user?.role === "admin";
  // 2026-08 · Staff = 内部员工 (admin/外销/销售主管/产品经理/采购/工厂经理/gt_export).
  // Non-staff (customer / factory) can browse tags but not create/edit them.
  // 2026-06 · product_manager has READ-ONLY access to tags (user spec) → not
  // part of the tag-creating staff set.
  const isStaff = ["admin", "export", "sales_manager", "production_manager", "buyer", "gt_export"].includes(user?.role);
  const canEditTag = (tg) => isAdmin || (tg?.created_by && tg.created_by === user?.email);

  const [tags, setTags] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [tagSearch, setTagSearch] = useState("");
  const [activeTag, setActiveTag] = useState(null); // null = 全部产品
  const [products, setProducts] = useState([]);
  const [prodBusy, setProdBusy] = useState(false);
  // 2026-07-31 · Batch-select mode inside the right-side product panel.
  const [selected, setSelected] = useState(() => new Set());
  const toggleSelect = (item) => setSelected((s) => {
    const next = new Set(s);
    next.has(item) ? next.delete(item) : next.add(item);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [addTagName, setAddTagName] = useState("");

  // Dialogs
  const [editing, setEditing] = useState(null); // {id, name, color} for edit or {name:'',color} for create
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [shareTag, setShareTag] = useState(null);
  // 2026-02 · Collapsible sidebar (user spec "标签栏可以隐藏"). Persist the
  // collapsed state in localStorage so it stays out of the way across reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("tagsPage.sidebarCollapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("tagsPage.sidebarCollapsed", sidebarCollapsed ? "1" : "0"); } catch {}
  }, [sidebarCollapsed]);
  // Human-friendly YYYY-MM-DD from an ISO timestamp — used in the tag row
  // subtitle so users see when + by whom a tag was created.
  const fmtDate = (iso) => (iso || "").slice(0, 10);

  const loadTags = async () => {
    try {
      // 2026-02 · Sort by newest first (user spec) — creation timestamp DESC.
      // 2026-06 · ``mine=true`` keeps this page consistent with the batch tag
      // picker: only tags I created or that were shared with me.
      // 2026-06 · 产品管理员 has read-only access and owns no tags, so they must
      // see the whole tag list instead of the empty "mine" slice.
      const { data } = await api.get("/tags", { params: { sort: "newest", mine: user?.role !== "product_manager" } });
      setTags(data.tags || []);
      setTotalProducts(data.total_products || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // 2026-02 · 外销 / 采购 只能看到 name 匹配自己的产品（user spec）。其他角色照旧看全部。
  // Backend `mine=1` param filters to products where seller OR buyer equals
  // the caller's user.name (case-insensitive), which is exactly the rule
  // requested here — no need to reimplement client-side.
  const restrictToOwn = ["export", "gt_export", "buyer"].includes(user?.role);
  // 2026-06 · 我的产品 toggle — defaults ON for 外销/采购 (their own catalog) but
  // 2026-08: always clickable so they can browse everything when needed.
  const [mineOnly, setMineOnly] = useState(restrictToOwn);
  // true when 我的产品 returned nothing and we fell back to the full catalog
  const [mineFellBack, setMineFellBack] = useState(false);

  const loadProducts = async () => {
    setProdBusy(true);
    try {
      const params = { page_size: 200 };
      if (activeTag) params.tag_filter = activeTag.name;
      if (mineOnly) params.mine = "1";
      const { data } = await api.get("/products", { params });
      let items = Array.isArray(data) ? data : (data.items || []);
      // 2026-08 · 没有属于自己的产品时回退显示全部，避免空白页
      let fellBack = false;
      if (mineOnly && items.length === 0) {
        const { data: all } = await api.get("/products", {
          params: { page_size: 200, ...(activeTag ? { tag_filter: activeTag.name } : {}) },
        });
        items = Array.isArray(all) ? all : (all.items || []);
        fellBack = items.length > 0;
      }
      setMineFellBack(fellBack);
      setProducts(items);
      clearSelection();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setProdBusy(false);
    }
  };

  useEffect(() => { loadTags(); }, []);
  useEffect(() => { loadProducts(); /* eslint-disable-next-line */ }, [activeTag, mineOnly]);

  const filteredTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    return q ? tags.filter((x) => x.name.toLowerCase().includes(q)) : tags;
  }, [tags, tagSearch]);

  const saveTag = async () => {
    if (!editing?.name?.trim()) { toast.error(t("tags.emptyName") || "标签名不能为空"); return; }
    try {
      if (editing.id) {
        await api.patch(`/tags/${editing.id}`, { name: editing.name.trim(), color: editing.color });
      } else {
        await api.post("/tags", { name: editing.name.trim(), color: editing.color });
      }
      toast.success(t("tags.saved") || "已保存");
      setEditing(null);
      loadTags();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/tags/${confirmDelete.id}`);
      toast.success(t("tags.deleted") || "已删除");
      setConfirmDelete(null);
      if (activeTag?.id === confirmDelete.id) setActiveTag(null);
      loadTags();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runBatch = async (action, tagName) => {
    if (!selected.size || !tagName) return;
    setBatchBusy(true);
    try {
      const { data } = await api.post("/tags/batch", {
        item_numbers: [...selected],
        tag_name: tagName,
        action,
      });
      toast.success(`${action === "add" ? (t("tags.batchAdded") || "已添加标签") : (t("tags.batchRemoved") || "已移除标签")}: ${data.updated}`);
      setAddTagOpen(false);
      setAddTagName("");
      await loadTags();
      await loadProducts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBatchBusy(false);
    }
  };

  // 2026-02 · Add every selected product to the current active quote. If none
  // is active, pop the QuotePicker so the user can pick / create one — same
  // pattern ProductsList uses for consistency.
  const addSelectedToQuote = async (quote) => {
    if (!selected.size) return;
    setAddBusy(true);
    try {
      let used = quote || activeQuote;
      if (!used) {
        // Defer to picker dialog; it will call this back via handlePickQuote.
        setPendingItems([...selected]);
        setPickerOpen(true);
        return;
      }
      for (const item of [...selected]) {
        used = (await addToQuote(item, quote || undefined)) || used;
        quote = undefined;
      }
      toast.success(`${selected.size} ${t("products.addedToQuote") || "个产品已加入"} ${used.quote_number || ""}`);
      clearSelection();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddBusy(false);
    }
  };
  const handlePickQuote = async (quote) => {
    setPickerOpen(false);
    if (!pendingItems.length) return;
    await addSelectedToQuote(quote);
    setPendingItems([]);
  };


  return (
    <div className="animate-fade-in-up" data-testid="tags-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ChevronLeft size={14} /> {t("tags.title") || "标签管理"}
      </button>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* ------------------------------- LEFT · Tag dictionary ---------------------- */}
        {/* 2026-02 · The whole sidebar collapses to a slim rail with an
            expand handle. Right panel then flexes to fill the width. */}
        {sidebarCollapsed ? (
          <button
            type="button"
            onClick={() => setSidebarCollapsed(false)}
            className="hidden lg:flex items-center justify-center w-8 shrink-0 rounded-md border border-border hover:border-primary hover:text-primary transition-colors h-fit self-stretch"
            title={t("tags.showSidebar") || "显示标签栏"}
            data-testid="tags-sidebar-expand"
          >
            <PanelLeftOpen size={14} />
          </button>
        ) : (
        <aside className="w-full lg:w-[320px] shrink-0 bg-card border border-border rounded-lg p-4 space-y-3 h-fit" data-testid="tags-left-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-sm font-bold">
              {t("tags.allTags") || "所有标签"} <span className="text-muted-foreground font-mono">{tags.length}</span>
            </h2>
            <Button
              variant="ghost" size="icon"
              className="h-7 w-7 hidden lg:inline-flex"
              onClick={() => setSidebarCollapsed(true)}
              title={t("tags.hideSidebar") || "隐藏标签栏"}
              data-testid="tags-sidebar-collapse"
            >
              <PanelLeftClose size={13} />
            </Button>
          </div>
          {isStaff && (
            <Button
              variant="outline"
              className="w-full h-9 text-xs font-medium border-dashed"
              onClick={() => setEditing({ name: "", color: PALETTE[tags.length % PALETTE.length] })}
              data-testid="tags-add-btn"
            >
              <Plus size={14} className="mr-1.5" /> {t("tags.addNew") || "新增标签"}
            </Button>
          )}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              placeholder={t("tags.searchTag") || "搜索标签"}
              className="h-8 text-xs pl-7"
              data-testid="tags-search-input"
            />
          </div>
          <ul className="space-y-1 max-h-[520px] overflow-y-auto pr-1">
            <li>
              <button
                type="button"
                onClick={() => setActiveTag(null)}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs transition-colors ${!activeTag ? "bg-primary/10 text-primary font-semibold" : "hover:bg-accent"}`}
                data-testid="tags-item-all"
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                  {t("tags.allProducts") || "全部产品"}
                </span>
                <span className="font-mono text-muted-foreground">{totalProducts}</span>
              </button>
            </li>
            {filteredTags.map((tg) => (
              <li key={tg.id}>
                <div className={`group flex items-start gap-1 rounded-md ${activeTag?.id === tg.id ? "bg-primary/10" : "hover:bg-accent"}`}>
                  <button
                    type="button"
                    onClick={() => setActiveTag(tg)}
                    className={`flex-1 flex items-start justify-between gap-2 px-2 py-1.5 text-xs transition-colors ${activeTag?.id === tg.id ? "text-primary font-semibold" : ""}`}
                    data-testid={`tags-item-${tg.name}`}
                  >
                    <span className="flex flex-col gap-0.5 min-w-0 flex-1 text-left">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tg.color }} />
                        <span className="truncate">{tg.name}</span>
                        {tg.shared_with?.length > 0 ? (
                          <Users size={11} className="text-blue-500 shrink-0" title={tg.shared_with.join(", ")} data-testid={`tags-shared-${tg.name}`} />
                        ) : null}
                        {tg.is_owner === false ? (
                          <span className="text-[9px] px-1 rounded bg-blue-500/10 text-blue-600 shrink-0">{t("tags.sharedToMe")}</span>
                        ) : null}
                      </span>
                      {/* 2026-02 · Creator + creation date — helps users
                          identify who owns a tag when the sidebar has many
                          entries from different teammates. */}
                      {(tg.created_by_name || tg.created_at) && (
                        <span className="text-[10px] text-muted-foreground font-normal ml-4 truncate" data-testid={`tags-meta-${tg.name}`}>
                          {fmtDate(tg.created_at)}{tg.created_by_name ? ` · ${tg.created_by_name}` : ""}
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-muted-foreground shrink-0 pt-0.5">{tg.product_count}</span>
                  </button>
                  {canEditTag(tg) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`tags-menu-${tg.name}`}>
                          <MoreHorizontal size={13} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-36">
                        <DropdownMenuItem onClick={() => setEditing({ id: tg.id, name: tg.name, color: tg.color })}>
                          <Edit2 size={12} className="mr-2" /> {t("tags.rename") || "重命名"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditing({ id: tg.id, name: tg.name, color: tg.color, focus: "color" })}>
                          <Palette size={12} className="mr-2" /> {t("tags.changeColor") || "改颜色"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShareTag(tg)} data-testid={`tags-share-${tg.name}`}>
                          <Share2 size={12} className="mr-2" /> {t("tags.share")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setConfirmDelete(tg)} className="text-destructive">
                          <Trash2 size={12} className="mr-2" /> {t("tags.delete") || "删除"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </aside>
        )}

        {/* ------------------------------ RIGHT · Products under tag ------------------ */}
        <section className="flex-1 min-w-0 bg-card border border-border rounded-lg p-4 space-y-4" data-testid="tags-right-panel">
          <header className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-heading text-base font-bold flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: activeTag?.color || "#94a3b8" }} />
                {t("tags.label") || "标签"}: {activeTag?.name || (t("tags.allProducts") || "全部产品")}
                {/* 2026-06 · 我的产品 — restricts the panel to products whose
                    外销 or 采购 equals the logged-in user's name. */}
                <Button
                  size="sm"
                  variant={mineOnly ? "default" : "outline"}
                  className="h-7 text-xs ml-1"
                  onClick={() => setMineOnly((v) => !v)}
                  data-testid="tags-mine-only-btn"
                  title={t("tags.myProductsHint")}
                >
                  <UserRound size={12} className="mr-1" /> {t("tags.myProducts")}
                </Button>
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="tags-count">
                {products.length} {t("tags.productsShown") || "个产品"}
                {mineFellBack && (
                  <span className="ml-2 text-amber-600" data-testid="tags-mine-fallback-hint">
                    · {t("tags.myProductsFallback")}
                  </span>
                )}
                {selected.size > 0 && (
                  <span className="ml-2 text-primary font-mono">· {t("tags.selectedCount") || "已选"} {selected.size}</span>
                )}
              </p>
            </div>
            {/* 2026-07-31 · Batch action bar. Only surfaced once one or more
                products are selected. Available to every staff role (2026-08 spec)
                so any user can bulk-tag their own catalog view. */}
            {selected.size > 0 && (
              <div className="flex items-center gap-1.5" data-testid="tags-batch-bar">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => addSelectedToQuote()}
                  disabled={addBusy}
                  data-testid="tags-add-to-quote-btn"
                >
                  <FilePlus2 size={12} className="mr-1" /> {t("tags.addToQuote") || "加入报价单"}
                </Button>
                {activeTag && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs text-destructive border-destructive/40 hover:bg-destructive/10"
                    onClick={() => runBatch("remove", activeTag.name)}
                    disabled={batchBusy}
                    data-testid="tags-batch-remove-btn"
                  >
                    <Trash2 size={12} className="mr-1" /> {t("tags.batchRemoveTag") || "从此标签移除"}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs"
                  onClick={clearSelection}
                  data-testid="tags-batch-clear-btn"
                >
                  {t("common.clear") || "取消选中"}
                </Button>
              </div>
            )}
          </header>

          {prodBusy ? (
            <div className="py-12 text-center text-xs text-muted-foreground">{t("common.loading") || "加载中…"}</div>
          ) : products.length === 0 ? (
            <div className="py-16 text-center text-xs text-muted-foreground">{t("tags.empty") || "此标签下暂无产品"}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {products.map((p) => {
                const isSelected = selected.has(p.item_number);
                return (
                  <div
                    key={p.item_number}
                    className={`relative bg-background border rounded-lg overflow-hidden transition-all ${isSelected ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/60"}`}
                    data-testid={`tags-product-card-${p.item_number}`}
                  >
                    {/* Selection checkbox — top-left overlay. Available to every
                        role so batch tagging works for all users (2026-08 spec). */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleSelect(p.item_number); }}
                      className={`absolute top-1.5 left-1.5 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? "bg-primary border-primary text-white" : "bg-white/90 border-border hover:border-primary"}`}
                      data-testid={`tags-select-${p.item_number}`}
                      title={isSelected ? "取消选中" : "选中"}
                    >
                      {isSelected && <Check size={12} strokeWidth={3} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // 2026-02 · Preserve the current scope (active tag + mine-only
                        // filter) on the URL so ProductForm's ⬅➡ walks THIS list
                        // instead of the whole catalog. Empty scope → no query string.
                        const qp = new URLSearchParams();
                        if (activeTag?.name) qp.set("tag", activeTag.name);
                        if (restrictToOwn) qp.set("mine", "1");
                        const qs = qp.toString();
                        navigate(`/products/${encodeURIComponent(p.item_number)}/edit${qs ? `?${qs}` : ""}`);
                      }}
                      className="w-full text-left"
                    >
                      <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
                        {p.photo_url ? (
                          <img
                            src={photoThumb(p.photo_url)}
                            alt={p.description}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">{t("products.noPhoto") || "无图"}</span>
                        )}
                      </div>
                      <div className="p-2.5 space-y-1">
                        <p className="text-xs font-mono font-semibold truncate">{p.item_number}</p>
                        <p className="text-[11px] text-muted-foreground uppercase truncate">{p.description}</p>
                        {p.spec_size && <p className="text-[10px] text-muted-foreground truncate">{p.spec_size}</p>}
                        {(p.tags?.length || p.tag) && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {(p.tags?.length ? p.tags : [p.tag]).filter(Boolean).slice(0, 3).map((tName) => {
                              const tg = tags.find((x) => x.name === tName);
                              return (
                                <span key={tName} className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: (tg?.color || "#94a3b8") + "20", color: tg?.color || "#64748b" }}>
                                  {tName}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* ------------------ Create / rename / recolor dialog --------------------- */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="tags-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? (t("tags.editTitle") || "编辑标签") : (t("tags.newTitle") || "新增标签")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("tags.name") || "标签名"}</label>
              <Input
                autoFocus={!editing?.focus}
                value={editing?.name || ""}
                onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                maxLength={32}
                data-testid="tags-edit-name"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t("tags.color") || "颜色"}</label>
              <div className="grid grid-cols-6 gap-2 mt-1.5" data-testid="tags-edit-palette">
                {PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setEditing((s) => ({ ...s, color: c }))}
                    className={`h-8 rounded-md border-2 transition-all ${editing?.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    data-testid={`tags-edit-color-${c.slice(1)}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel") || "取消"}</Button>
            <Button onClick={saveTag} data-testid="tags-edit-save">{t("common.save") || "保存"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------- Delete confirm --------------------------- */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="max-w-sm" data-testid="tags-delete-dialog">
          <DialogHeader>
            <DialogTitle>{t("tags.deleteTitle") || "删除标签"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("tags.deleteConfirm") || "确定删除标签"} <b>{confirmDelete?.name}</b>？
            <br />
            <span className="text-xs">{t("tags.deleteNote") || "标签会被隐藏，已打此标签的产品数据保留。"}</span>
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>{t("common.cancel") || "取消"}</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="tags-delete-confirm">{t("common.delete") || "删除"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- 2026-07-31 · Batch "add tag" picker (shared component) --------- */}
      <TagPickerDialog
        open={addTagOpen}
        onOpenChange={setAddTagOpen}
        selectedCount={selected.size}
        excludeTag={activeTag?.name}
        busy={batchBusy}
        onConfirm={(name) => runBatch("add", name)}
      />
      {/* 2026-06 · Per-tag sharing (same dialog as the batch picker). */}
      <ShareTagDialog tag={shareTag} onClose={() => setShareTag(null)} onSaved={loadTags} />
      {/* 2026-02 · Quote picker for "加入报价单" when no active quote is set. */}
      <QuotePicker open={pickerOpen} onOpenChange={setPickerOpen} onSelect={handlePickQuote} />
    </div>
  );
}
